import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import { useCallback, useMemo } from "react";
import { COLUMN_WIDTH, type IconName } from "@salestrak/ui";
import {
  defaultBoardGrouping,
  type IssueScope,
} from "#app/lib/filters/issue-scope";
import {
  useAnalytics,
  AnalyticsContext,
  IssueEvent,
  AnalyticsView,
} from "#app/lib/analytics";
import { ASSIGNEE_ICON } from "#app/lib/entity-visuals";
import {
  ISSUE_SORT_DEFAULT_DIRECTION,
  ISSUE_SORT_ICONS,
  ISSUE_SORT_LABELS,
} from "#app/components/issues/issue-grouping";
import {
  toBoardGroupDimension,
  type BoardGroupDimension,
} from "#app/components/issues/board-columns";
import type { BoardCardSize, ViewConfig } from "#app/lib/view-config";

export const BOARD_CARD_SIZE_WIDTHS: Record<BoardCardSize, number> = {
  medium: 264,
  large: COLUMN_WIDTH,
};

// Manual board order — the drag-set sortOrder. Stable module reference so columns
// don't re-subscribe on every render.
const ORDER_BY: Record<string, unknown> = { sortOrder: 1 };

const ALL_GROUP_BY_LABELS: Record<BoardGroupDimension, string> = {
  stage: "Stage",
  workflow: "Workflow",
  assignee: "Assignee",
  team: "Assignee Team",
  teams: "Teams",
  age: "Follow up",
};

const ALL_GROUP_BY_ICONS: Partial<Record<string, IconName>> = {
  stage: PROPERTY_ICON.stage,
  workflow: PROPERTY_ICON.workflow,
  assignee: ASSIGNEE_ICON,
  team: PROPERTY_ICON.team,
  teams: PROPERTY_ICON.team,
  age: PROPERTY_ICON.followUp,
};

// The dimensions offered per scope. A single workflow groups by stage; a
// cross-workflow scope (team/member) groups by workflow instead. `teams` is
// the DIRECT Teams property; `team` (assignee-membership expansion) is
// filter-only — it double-counts an issue whose assignees span teams.
const WORKFLOW_SCOPE_DIMENSIONS: BoardGroupDimension[] = [
  "stage",
  "assignee",
  "teams",
  "age",
];
const CROSS_WORKFLOW_DIMENSIONS: BoardGroupDimension[] = [
  "workflow",
  "assignee",
  "teams",
  "age",
];

function pickLabels<T>(
  source: Record<string, T>,
  keys: BoardGroupDimension[],
): Record<string, T> {
  const out: Record<string, T> = {};
  for (const k of keys) if (source[k] !== undefined) out[k] = source[k];
  return out;
}


/**
 * The board's grouping dimension and sort: the toolbar pill and the panel's
 * Group by / Sort by rows, read from and written to the view config.
 */
export function useIssuesBoardGrouping({
  scope,
  workflowId,
  config,
  setConfig,
}: {
  scope: IssueScope;
  workflowId: string | undefined;
  config: ViewConfig;
  setConfig: (patch: Partial<ViewConfig>) => void;
}) {
  const { track } = useAnalytics();

  // --- Grouping dimension + direction (the toolbar pill / the panel row) ---
  const dimensions = workflowId
    ? WORKFLOW_SCOPE_DIMENSIONS
    : CROSS_WORKFLOW_DIMENSIONS;
  const groupByLabels = useMemo(
    () => pickLabels(ALL_GROUP_BY_LABELS, dimensions),
    [dimensions],
  );
  const groupByIcons = useMemo(
    () => pickLabels(ALL_GROUP_BY_ICONS, dimensions),
    [dimensions],
  );
  const groupBy = useMemo(() => {
    const fallback = defaultBoardGrouping(scope);
    const stored = toBoardGroupDimension(config.groupBy, fallback);
    // A dimension this scope does not offer — a saved config naming `stage` on
    // a cross-workflow board — reads as the scope's default, not as a board
    // with no columns.
    return dimensions.includes(stored) ? stored : fallback;
  }, [config.groupBy, dimensions, scope]);
  const groupSortDirection = config.groupDirection;
  const groupByPanel = useMemo(
    () => ({
      labels: groupByLabels,
      icons: groupByIcons,
      // A board's columns ARE its grouping — there is no flat board.
      requireGrouping: true,
    }),
    [groupByLabels, groupByIcons],
  );

  // --- Sort (the panel's Sort by row) ---
  // Active only for a key the vocabulary names — a stale saved key reads as
  // unsorted rather than sending a selector the server would skip. While a
  // sort is active every column orders by it instead of the manual drag
  // order. Drops stay live for stage moves, but a same-column drop is refused
  // with a toast in handleDragEnd: it would write a manual sortOrder from
  // neighbors whose visual order no longer reflects it.
  const sortBy =
    config.sortBy !== "none" && config.sortBy in ISSUE_SORT_LABELS
      ? config.sortBy
      : "none";
  const sortActive = sortBy !== "none";
  const columnOrderBy = useMemo<Record<string, unknown>>(
    () =>
      sortActive
        ? { [sortBy]: config.sortDirection === "desc" ? -1 : 1 }
        : ORDER_BY,
    [sortActive, sortBy, config.sortDirection],
  );
  const sortByPanel = useMemo(
    () => ({
      labels: ISSUE_SORT_LABELS,
      icons: ISSUE_SORT_ICONS,
      defaultDirections: ISSUE_SORT_DEFAULT_DIRECTION,
    }),
    [],
  );
  // Picking a field also seats its natural direction (dates newest- or
  // soonest-first, names A-to-Z); the direction control overrides after.
  const setSortBy = useCallback(
    (field: string) => {
      const preferred = ISSUE_SORT_DEFAULT_DIRECTION[field];
      setConfig(
        preferred ? { sortBy: field, sortDirection: preferred } : { sortBy: field },
      );
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.ChangeSorting,
        data: { view: AnalyticsView.Board, scope: scope.kind, field, via: "toolbar" },
      });
    },
    [setConfig, track, scope.kind],
  );
  const setGroupBy = useCallback(
    (field: string) => {
      setConfig({ groupBy: field });
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.ChangeGrouping,
        data: {
          view: AnalyticsView.Board,
          scope: scope.kind,
          dimension: field,
          via: "toolbar",
        },
      });
    },
    [setConfig, track, scope.kind],
  );

  return {
    dimensions,
    groupBy,
    groupByLabels,
    groupByIcons,
    groupSortDirection,
    groupByPanel,
    sortBy,
    sortActive,
    columnOrderBy,
    sortByPanel,
    setSortBy,
    setGroupBy,
  };
}
